/**
 * The twelve finishing pigs in beat 10.
 *
 * These definitions are deliberately only the herd's voice. The director owns
 * when they start and stop; rendering a deterministic graph here lets the audio
 * gate judge the exact same sound which is later spatialised in the field.
 */

import { connectChain, filter, gain, lfo, lfoOut, noiseSource } from '../synth';
import type { SoundDefinition } from '../types';

/**
 * When the herd calls after her, in SIMULATION seconds from the stall latching.
 *
 * ── Why three, and then nothing ─────────────────────────────────────────────
 *
 * A stall can last forty seconds — the whole rest of beat 10's schedule — and a
 * call every three of them is a metronome, which `docs/audio-direction.md`
 * names as the failure mode of anything that repeats. It would also go on saying
 * the same sentence long after the player has understood it. So the group calls
 * three times and gives up, and the silence afterwards is worse than the calling
 * was.
 *
 * The first is close enough to the cause to BE the cause. `STALL_GRACE` has
 * already spent 0.6 s deciding, so a quarter second after that is about eight
 * tenths after she opened the gap — inside the window where a player attributes
 * an effect to what they just did. The gaps then WIDEN, which is the opposite of
 * the dog schedule's shape and deliberately so: that is something arriving and
 * this is something being abandoned.
 *
 * Here rather than in `main.ts` so `beat-10-stall.spec.ts` can assert the exact
 * number of calls against the constant that decides it. `docs/traps.md`: derive
 * an expected value from the exported constant the code reads.
 */
export const HERD_CALL_AT: readonly number[] = [0.25, 3.0, 7.0];

/**
 * Playback gain for the call and for the bed, and the RATIO is the design.
 *
 * The call is nearly four times the bed. `herd-shuffle` exists to be tuned out —
 * twelve animals walking behind her for most of a stage — so that when it stops
 * and one call arrives the player has heard a *change* rather than a sound. A
 * bed loud enough to notice on its own would have spent that.
 */
export const HERD_CALL_GAIN = 0.85;
export const HERD_SHUFFLE_GAIN = 0.22;

/**
 * Seconds the bed takes to reach a new level, in simulation time.
 *
 * `CulvertQueueSnapshot.drawn` is a per-STEP fact and a render frame samples
 * whichever step was last. At 60 Hz that is stable while she leads, but a frame
 * landing on the one step in a hundred where the advance clamped to zero would
 * cut the bed for a frame, and a bed switched at frame rate is a buzz.
 */
export const HERD_SHUFFLE_SLEW = 0.35;

/**
 * How far above the ground the herd's voices sit, metres — a finisher's head.
 *
 * It matters more than it looks: `PannerNode` gives distance attenuation from
 * this point, and a source authored at y = 0 on beat 10's 4 % fall is a source
 * that sinks as the string descends.
 */
export const HERD_VOICE_HEIGHT = 0.45;

/**
 * Twelve bodies travelling through wet grass, heard from in front of the file.
 *
 * This is a bed rather than twelve footfalls. Discrete contacts would turn a
 * twenty-metre string into a metronome, making the animals sound coordinated
 * and close enough to count by ear; the player needs to hear a mass continuing
 * behind her, not a rhythm that competes with Rosalie's own stealth footfalls.
 */
const herdShuffle: SoundDefinition = {
  id: 'herd-shuffle',
  label: 'Herd shuffling through wet grass',
  description: 'A continuous, uneven low grass-and-body bed from the file of pigs behind Rosalie.',
  category: 'creature',
  duration: 4,
  loop: true,
  render(ctx) {
    const out = gain(ctx, 2);
    connectChain(out, ctx.destination);

    const grass = gain(ctx, 0.18);
    connectChain(
      // Seeded, so the render and the measurement are the same bed every time.
      //
      // **`noiseBuffer`'s crossfade does NOT make the wrap continuous, and this
      // comment used to claim it did.** It blends the last 512 samples toward
      // `data[i]`, so the final sample lands on `data[511]` while the loop
      // restarts at `data[0]` — a random step. Every enveloped noise sound in
      // this game hides that under its own decay; a continuous bed does not, and
      // this one measures a **0.0501 loop seam against a 0.1 threshold**, which
      // is by far the loosest in the file. It is audible as a soft tick once
      // every four seconds if you listen for it. Fixing `noiseBuffer` changes
      // the samples of every noise sound in the game and is filed against its
      // own session (`docs/backlog.md`).
      noiseSource(ctx, 4, 10_101),
      filter(ctx, { type: 'lowpass', frequency: 900, q: 0.7 }),
      filter(ctx, { type: 'bandpass', frequency: 300, q: 0.65 }),
      grass,
      out,
    );

    const body = gain(ctx, 0.055);
    connectChain(
      noiseSource(ctx, 4, 10_237),
      // This is weight under the grass, not a second gust of wind; keeping it
      // low and well below the grass layer gives the file a moving mass.
      filter(ctx, { type: 'lowpass', frequency: 180, q: 0.7 }),
      body,
      out,
    );

    // All rates divide the four-second loop exactly, so their values return to
    // the same gains at its seam. Their shallow, unsynchronised movement is the
    // only motion: adding pitched voices or separate steps would make this bed
    // memorable enough to become in-farm music.
    lfoOut(lfo(ctx, 0.25, 0.045)).connect(grass.gain);
    lfoOut(lfo(ctx, 0.5, 0.0216)).connect(grass.gain);
    lfoOut(lfo(ctx, 0.75, 0.0055)).connect(body.gain);
  },
};

/** Schedule the voiced body of one finishing pig's low, nasal contact grunt. */
function gruntVoice(
  ctx: BaseAudioContext,
  out: AudioNode,
  offset: number,
  duration: number,
  fundamental: number,
): void {
  const t0 = ctx.currentTime + offset;
  const shape = gain(ctx, 0);
  // The 1.15 kHz formant is deliberately the top of the ordinary voice, not a
  // source of accidental brightness: a bandpass has an infinite tail, so this
  // explicit cap leaves the third pig's short 2.1 kHz edge as the sole exception.
  connectChain(shape, filter(ctx, { type: 'lowpass', frequency: 1500, q: 0.7 }), out);

  // Four unrelated fundamentals avoid a chord. These are animals responding at
  // different distances, not a call-and-response phrase the scene would mistake
  // for score.
  for (const [partial, level] of [[1, 0.72], [2, 0.19], [3, 0.07]] as const) {
    const source = ctx.createOscillator();
    source.type = 'sine';
    source.frequency.setValueAtTime(fundamental * partial, t0);
    // The contour asks a question and then leaves it unanswered: it rises just
    // long enough to seek Rosalie, then falls fifteen percent as the herd stops.
    source.frequency.linearRampToValueAtTime(fundamental * partial * 1.08, t0 + duration / 3);
    source.frequency.linearRampToValueAtTime(fundamental * partial * 0.85, t0 + duration);
    source.start(t0);
    source.stop(t0 + duration);

    // Two parallel short-tract formants are nasal rather than squealing: the
    // lower one carries the animal and the 1.15 kHz one is deliberately weaker.
    const lowFormant = gain(ctx, 0.8 * level);
    connectChain(
      source,
      filter(ctx, { type: 'bandpass', frequency: 480, q: 2.1 }),
      lowFormant,
      shape,
    );
    const highFormant = gain(ctx, 0.25 * level);
    connectChain(
      source,
      filter(ctx, { type: 'bandpass', frequency: 1150, q: 2.3 }),
      highFormant,
      shape,
    );
  }

  shape.gain.setValueAtTime(0, t0);
  shape.gain.linearRampToValueAtTime(0.38, t0 + 0.012);
  shape.gain.setValueAtTime(0.38, t0 + duration - 0.12);
  shape.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  shape.gain.setValueAtTime(0, t0 + duration);

  // A little air at contact makes the grunt a mouth opening, not a clean
  // oscillator. It is eighteen decibels under the voiced body and over before
  // the pitch contour begins to read as a call.
  const breath = gain(ctx, 0);
  connectChain(
    noiseSource(ctx, 2.2, 10_501 + Math.round(offset * 100)),
    filter(ctx, { type: 'bandpass', frequency: 900, q: 0.7 }),
    // As with the voiced formants, the bandpass alone has a high tail; cap the
    // breath explicitly so it cannot become a second bright exception.
    filter(ctx, { type: 'lowpass', frequency: 1500, q: 0.7 }),
    breath,
    out,
  );
  breath.gain.setValueAtTime(0, t0);
  breath.gain.linearRampToValueAtTime(0.048, t0 + 0.006);
  breath.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.04);
  breath.gain.setValueAtTime(0, t0 + 0.045);
}

/** Four pigs noticing that Rosalie has opened the gap and left them behind. */
const herdStall: SoundDefinition = {
  id: 'herd-stall',
  label: 'Herd stalled',
  description: 'Four low, nasal finishing-pig grunts falling away into an empty field.',
  category: 'creature',
  duration: 2.2,
  loop: false,
  render(ctx) {
    const out = gain(ctx, 4);
    connectChain(out, ctx.destination);

    const voices: readonly [number, number, number][] = [
      [0, 0.46, 128],
      [0.18, 0.42, 152],
      [0.55, 0.5, 176],
      [1.05, 0.4, 141],
    ];
    for (const [offset, duration, fundamental] of voices) {
      gruntVoice(ctx, out, offset, duration, fundamental);
    }

    // This is the ONLY element above 1.5 kHz. It belongs to the third animal's
    // first 90 ms, a thin edge of almost-squeal that makes one voice catch; any
    // other bright component would turn the group from heavy pigs into alarms.
    const bright = ctx.createOscillator();
    bright.type = 'sine';
    bright.frequency.value = 2100;
    const brightShape = gain(ctx, 0);
    connectChain(bright, brightShape, out);
    brightShape.gain.setValueAtTime(0, ctx.currentTime + 0.55);
    brightShape.gain.linearRampToValueAtTime(0.075, ctx.currentTime + 0.558);
    brightShape.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.64);
    brightShape.gain.setValueAtTime(0, ctx.currentTime + 0.645);
    bright.start(ctx.currentTime + 0.55);
    bright.stop(ctx.currentTime + 0.65);

    // The final voice ends at 1.45 s. The remaining 0.75 s is intentional
    // trailing silence: she has gone too far, and the field does not answer.
  },
};

export const herdSounds: readonly SoundDefinition[] = [herdShuffle, herdStall];
